console.log("arbre de contenu");

/**
 * tag-arbre.js
 * Animated guided-assistance tree navigation — vanilla JS, fully accessible.
 *
 * Corrections accessibilité (v2) :
 *  - aria-expanded mis à jour (false → true) après sélection d'une carte
 *  - Focus repositionné sur le titre introductif (.tag-arbre-intro) après chaque choix
 *  - tabindex="-1" sur contentArea pour permettre le focus programmatique au reset
 *  - Touche Escape : remonte d'un niveau, ou réinitialise si on est au niveau 1
 *  - Zone aria-live dédiée pour annoncer le contenu des nœuds feuilles
 *  - Titre de l'arbre passé en <h3> (aria-level="3")
 *  - aria-label du breadcrumb relié au <h4> via aria-labelledby
 *
 * Version imprimable (v3) :
 *  - Génère au chargement une version statique, à plat, entièrement dépliée
 *    de l'arbre (classe .tag-arbre-print, masquée à l'écran via .print__only)
 *  - Le widget interactif est masqué à l'impression via CSS (_print.scss)
 *
 * Internationalisation : override window.tagArbreI18n avant le chargement du script.
 *   window.tagArbreI18n = { yourSelection: 'Your selection:', reset: 'Reset' };
 */

(function () {
  'use strict';

  /* ─── i18n ─────────────────────────────────────────────────────────────── */
  const i18n = Object.assign(
    { yourSelection: 'Votre sélection\u00a0:', reset: 'Réinitialiser' },
    window.tagArbreI18n || {}
  );

  /* ─── helpers ───────────────────────────────────────────────────────────── */
  const qs  = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  /** Retourne les <li> enfants directs d'un <ul>. */
  function childNodes(ul) {
    return ul ? qsa(':scope > li', ul) : [];
  }

  /**
   * Fait défiler un élément dans la fenêtre uniquement s'il est partiellement
   * hors de l'écran — évite les sauts visuels intempestifs.
   */
  function scrollIntoViewIfNeeded(el) {
    const rect = el.getBoundingClientRect();
    if (rect.top < 0 || rect.bottom > window.innerHeight) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /* ─── Version imprimable ───────────────────────────────────────────────── */

  /**
   * Construit une version statique et entièrement dépliée de l'arbre pour
   * l'impression. Parcourt récursivement le <li> et retourne une <li>
   * contenant modalité + résumé + intro pour chaque nœud, et le contenu
   * complet pour les feuilles.
   */
  function buildPrintOutline(li, depth) {
    const bloc = qs('.bloc-arbre', li);
    if (!bloc) return null;

    const modalite = bloc.querySelector('.bloc-arbre__modalite')?.textContent ?? '';
    const resume   = bloc.querySelector('.bloc-arbre__resume')?.textContent   ?? '';
    const intro    = bloc.querySelector('.bloc-arbre__intro')?.innerHTML  ?? '';
    const info     = bloc.querySelector('.bloc-arbre__info')?.innerHTML  ?? '';

    const item = document.createElement('li');
    item.className = 'tag-arbre-print__item';
    item.style.setProperty('--depth', depth);

    if (modalite) {
      const title = document.createElement('p');
      title.className = 'tag-arbre-print__title';
      title.textContent = modalite;
      item.appendChild(title);
    }

    if (resume) {
      const res = document.createElement('p');
      res.className = 'tag-arbre-print__resume';
      res.textContent = resume;
      item.appendChild(res);
    }

    if (intro) {
      const introEl = document.createElement('div');
      introEl.className = 'tag-arbre-print__intro';
      introEl.innerHTML = intro;
      item.appendChild(introEl);
    }

    const directChildUl = qs(':scope > ul', li);
    const children = childNodes(directChildUl);

    if (children.length === 0) {
      /* Nœud feuille : affiche le contenu informatif complet */
      if (info) {
        const infoEl = document.createElement('div');
        infoEl.className = 'tag-arbre-print__info';
        infoEl.innerHTML = info;
        item.appendChild(infoEl);
      }
    } else {
      /* Nœud intermédiaire : continue la récursion sur les enfants */
      const childList = document.createElement('ul');
      childList.className = 'tag-arbre-print__list';
      children.forEach((childLi) => {
        const childItem = buildPrintOutline(childLi, depth + 1);
        if (childItem) childList.appendChild(childItem);
      });
      item.appendChild(childList);
    }

    return item;
  }

  /**
   * Génère et insère la version imprimable (à plat, tout déplié) de l'arbre,
   * masquée à l'écran (classe print__only déjà utilisée ailleurs dans le projet).
   */
  function insertPrintOutline(rootLi, wrapper, treeIndex) {
    const printRoot = document.createElement('ul');
    printRoot.className = 'tag-arbre-print print__only';
    printRoot.setAttribute('data-tree-index', treeIndex);

    const rootItem = buildPrintOutline(rootLi, 0);
    if (rootItem) {
      printRoot.appendChild(rootItem);
    }

    wrapper.appendChild(printRoot);
  }

  /* ─── Initialisation d'un arbre ─────────────────────────────────────────── */
  function initTree(rootUl, treeIndex) {
    const rootLi = qs(':scope > li', rootUl);
    if (!rootLi) return;

    const rootBloc = qs('.bloc-arbre', rootLi);
    if (!rootBloc) return;

    /* ── Wrapper principal ─────────────────────────────────────────────────── */
    const wrapper = document.createElement('div');
    wrapper.className = 'tag-arbre-widget';
    wrapper.setAttribute('data-tree-index', treeIndex);
    rootUl.parentNode.insertBefore(wrapper, rootUl);
    rootUl.remove();

    /* ── Zone d'annonce pour les feuilles (aria-live) ──────────────────────── */
    // Séparée de contentArea pour que l'annonce se déclenche même si le
    // contenu était déjà présent (remplacement de innerHTML).
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'visually-hidden';
    wrapper.appendChild(liveRegion);

    /* ── Zone fil d'Ariane ─────────────────────────────────────────────────── */
    const breadcrumbArea = document.createElement('div');
    breadcrumbArea.className = 'tag__arbre__choix--en--cours';
    breadcrumbArea.hidden = true;
    breadcrumbArea.setAttribute('aria-live', 'polite');
    breadcrumbArea.setAttribute('aria-atomic', 'false');

    /* ID unique pour relier le <h4> à l'<ol> via aria-labelledby */
    const bcTitleId = 'tag-arbre-bc-title-' + treeIndex;

    const bcTitle = document.createElement('h4');
    bcTitle.className = 'tag__arbre__choix--en--cours__titre';
    bcTitle.id = bcTitleId;
    bcTitle.textContent = i18n.yourSelection;

    const resetBtn = document.createElement('button');
    resetBtn.className = 'tag__arbre__choix--en--cours__annulation reset';
    resetBtn.textContent = i18n.reset;
    resetBtn.setAttribute('type', 'button');
    resetBtn.addEventListener('click', () => reset());

    const bcList = document.createElement('ol');
    bcList.className = 'tag__arbre__choix--en--cours__container__breadcrumb';
    /* CORRECTION : aria-labelledby pointe sur le <h4> (et non aria-label seul) */
    bcList.setAttribute('aria-labelledby', bcTitleId);

    breadcrumbArea.append(bcTitle, resetBtn, bcList);
    wrapper.appendChild(breadcrumbArea);

    /* ── Zone de contenu ───────────────────────────────────────────────────── */
    const contentArea = document.createElement('div');
    contentArea.className = 'tag-arbre-content';
    /* CORRECTION : tabindex="-1" permet le focus programmatique sur ce <div> */
    contentArea.setAttribute('tabindex', '-1');
    wrapper.appendChild(contentArea);

    /* ── État ──────────────────────────────────────────────────────────────── */
    let path = []; // tableau des <li> choisis, du plus haut au plus profond

    /* Référence vers le bouton de la dernière carte sélectionnée,
       pour remettre aria-expanded à "false" si l'on remonte dans l'arbre */
    let lastChosenCard = null;

    /* ── Escape délégué sur le wrapper ──────────────────────────────────────
       Un seul listener sur le wrapper capte la touche Escape quel que soit
       l'élément focusé à l'intérieur (intro, carte, breadcrumb, reset…).
       Les listeners individuels sur les cartes et boutons du breadcrumb
       sont donc inutiles et supprimés.
    ────────────────────────────────────────────────────────────────────────── */
    wrapper.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleEscape();
      }
    });

    /* ── Rendu ─────────────────────────────────────────────────────────────── */

    /**
     * Affiche le titre introductif du nœud courant et ses enfants sous forme
     * de cartes cliquables, ou le contenu informatif si c'est une feuille.
     */
    function renderLevel(li) {
      contentArea.innerHTML = '';

      const bloc  = qs('.bloc-arbre', li);
      const intro = bloc ? (bloc.querySelector('.bloc-arbre__intro')?.textContent ?? '') : '';
      const info  = bloc ? (bloc.querySelector('.bloc-arbre__info')?.innerHTML  ?? '')  : '';

      /* Titre introductif — reçoit le focus après une sélection */
      if (intro) {
        const heading = document.createElement('p');
        heading.className = 'tag-arbre-intro';
        heading.setAttribute('role', 'heading');
        /* CORRECTION : aria-level="3" conformément à la demande */
        heading.setAttribute('aria-level', '3');
        /* CORRECTION : tabindex="-1" pour permettre focus programmatique */
        heading.setAttribute('tabindex', '-1');
        heading.innerHTML = intro;
        contentArea.appendChild(heading);
      }

      const directChildUl = qs(':scope > ul', li);
      const children      = childNodes(directChildUl);

      if (children.length === 0) {
        /* Nœud feuille — affiche le contenu informatif */
        if (info) {
          const infoDiv = document.createElement('div');
          infoDiv.className = 'tag-arbre-info';
          infoDiv.innerHTML = info;
          contentArea.appendChild(infoDiv);

          /* CORRECTION : annonce le contenu feuille via la live region dédiée.
             On injecte un court résumé textuel (pas le HTML complet) pour éviter
             une lecture interminable. Le texte brut suffit à signaler l'arrivée. */
          const leafText = infoDiv.textContent.trim().slice(0, 120);
          liveRegion.textContent = '';
          /* Forcer une nouvelle insertion pour que la live region re-déclenche */
          requestAnimationFrame(() => {
            liveRegion.textContent = leafText + (infoDiv.textContent.length > 120 ? '…' : '');
          });
        }
        return;
      }

      /* Grille de cartes */
      const grid = document.createElement('div');
      grid.className = 'tag-arbre-options';
      grid.setAttribute('role', 'list');

      children.forEach((childLi) => {
        const childBloc = qs('.bloc-arbre', childLi);
        if (!childBloc) return;

        const modalite = childBloc.querySelector('.bloc-arbre__modalite')?.textContent ?? '';
        const resume   = childBloc.querySelector('.bloc-arbre__resume')?.textContent   ?? '';

        const isLeaf = !qs(':scope > ul', childLi);

        const card = document.createElement('button');
        card.setAttribute('type', 'button');
        card.className = 'tag-arbre-card';
        card.setAttribute('role', 'listitem');

        /* CORRECTION : aria-expanded uniquement sur les non-feuilles,
           initialisé à "false" ; sera mis à "true" lors du clic */
        if (!isLeaf) {
          card.setAttribute('aria-expanded', 'false');
        }

        card.setAttribute('aria-label',
          [modalite, resume].filter(Boolean).join(' — ') || modalite
        );

        const modaliteSpan = document.createElement('span');
        modaliteSpan.className = 'tag-arbre-card__modalite';
        modaliteSpan.textContent = modalite;
        card.appendChild(modaliteSpan);

        if (resume) {
          const resumeSpan = document.createElement('span');
          resumeSpan.className = 'tag-arbre-card__resume';
          resumeSpan.textContent = resume;
          card.appendChild(resumeSpan);
        }

        card.addEventListener('click', () => choose(childLi, card));

        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            choose(childLi, card);
          }
          /* Escape géré par délégation sur le wrapper */
        });

        grid.appendChild(card);
      });

      contentArea.appendChild(grid);
    }

    /**
     * Gestion de la touche Escape :
     * - Si on est au niveau 1 (un seul choix dans le path) → reset complet
     * - Sinon → remonte d'un niveau (backTo au niveau précédent)
     */
    function handleEscape() {
      if (path.length === 0) return;
      if (path.length === 1) {
        reset();
      } else {
        backTo(path.length - 2);
      }
    }

    /**
     * L'utilisateur a cliqué/tapé sur une carte.
     * @param {HTMLElement} chosenLi  - le <li> correspondant au choix
     * @param {HTMLElement} cardBtn   - le <button> carte cliqué (pour aria-expanded)
     */
    function choose(chosenLi, cardBtn) {
      /* CORRECTION : marque la carte précédemment choisie comme "collapsed"
         (si elle est encore dans le DOM — pas le cas ici car on reconstruit,
         mais on garde la référence pour un éventuel usage futur) */
      if (lastChosenCard && lastChosenCard.hasAttribute('aria-expanded')) {
        lastChosenCard.setAttribute('aria-expanded', 'false');
      }

      /* CORRECTION : marque la carte cliquée comme "expanded" */
      if (cardBtn && cardBtn.hasAttribute('aria-expanded')) {
        cardBtn.setAttribute('aria-expanded', 'true');
      }
      lastChosenCard = cardBtn;

      path.push(chosenLi);
      updateBreadcrumb();
      renderLevel(chosenLi);
      breadcrumbArea.hidden = false;

      /* CORRECTION : déplace le focus sur le titre introductif du nouveau niveau */
      const intro = qs('.tag-arbre-intro', contentArea);
      if (intro) {
        intro.focus();
      }

      scrollIntoViewIfNeeded(breadcrumbArea);
    }

    /** Reconstruit la liste du fil d'Ariane depuis le tableau path. */
    function updateBreadcrumb() {
      bcList.innerHTML = '';

      path.forEach((li, idx) => {
        const bloc     = qs('.bloc-arbre', li);
        const modalite = bloc?.querySelector('.bloc-arbre__modalite')?.textContent ?? '';
        const isCurrent = idx === path.length - 1;

        const item = document.createElement('li');
        item.className = 'tag__arbre__breadcrumb__item';

        /* Icône SVG (coche) — identique visuellement, distinguée par aria-current */
        const createIcon = () => {
          const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          svg.setAttribute('viewBox', '0 0 12 10');
          svg.setAttribute('class', 'tag__arbre__breadcrumb__icon');
          svg.setAttribute('aria-hidden', 'true');
          const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          pathEl.setAttribute('d', 'M1 5l3.5 3.5L11 1');
          pathEl.setAttribute('fill', 'none');
          pathEl.setAttribute('stroke', 'currentColor');
          pathEl.setAttribute('stroke-width', '2');
          pathEl.setAttribute('stroke-linecap', 'round');
          pathEl.setAttribute('stroke-linejoin', 'round');
          svg.appendChild(pathEl);
          return svg;
        };

        if (isCurrent) {
          const span = document.createElement('span');
          span.className = 'tag__arbre__breadcrumb__current';
          /* aria-current="step" communique l'état courant aux lecteurs d'écran.
             Pour la différenciation visuelle non-couleur, appliquer en CSS :
             .tag__arbre__breadcrumb__current .tag__arbre__breadcrumb__icon { … }
             ex. : stroke-width différent, fond plein sur l'icône, etc. */
          span.setAttribute('aria-current', 'step');
          span.appendChild(createIcon());
          span.appendChild(document.createTextNode(modalite));
          item.appendChild(span);
        } else {
          const btn = document.createElement('button');
          btn.setAttribute('type', 'button');
          btn.className = 'tag__arbre__breadcrumb__link';
          btn.appendChild(createIcon());
          btn.appendChild(document.createTextNode(modalite));
          btn.addEventListener('click', () => backTo(idx));
          /* Escape géré par délégation sur le wrapper */

          item.appendChild(btn);
        }

        bcList.appendChild(item);
      });
    }

    /**
     * Remonte à la profondeur idx (0-indexé) et re-rend ce niveau.
     */
    function backTo(idx) {
      path = path.slice(0, idx + 1);
      lastChosenCard = null;
      updateBreadcrumb();
      renderLevel(path[path.length - 1]);

      /* CORRECTION : focus sur le titre introductif du niveau retrouvé */
      const intro = qs('.tag-arbre-intro', contentArea);
      if (intro) intro.focus();

      scrollIntoViewIfNeeded(breadcrumbArea);
    }

    /** Réinitialisation complète. */
    function reset() {
      path = [];
      lastChosenCard = null;
      breadcrumbArea.hidden = true;
      bcList.innerHTML = '';
      liveRegion.textContent = '';
      renderLevel(rootLi);

      /* CORRECTION : contentArea est focusable grâce à tabindex="-1" */
      contentArea.focus();
      scrollIntoViewIfNeeded(wrapper);
    }

    /* ── Titre de l'arbre (niveau 0) ─────────────────────────────────────────
       CORRECTION : aria-level="3" (était "2") conformément à la demande.
       tag-arbre-title et tag-arbre-summary sont en sr-only dans le CSS — ils
       servent uniquement aux lecteurs d'écran pour contextualiser l'arbre.
    ───────────────────────────────────────────────────────────────────────── */
    const rootModalite = rootBloc.querySelector('.bloc-arbre__modalite')?.textContent ?? '';
    const rootResume   = rootBloc.querySelector('.bloc-arbre__resume')?.textContent   ?? '';

    if (rootModalite) {
      const treeTitle = document.createElement('p');
      treeTitle.className = 'tag-arbre-title';
      treeTitle.setAttribute('role', 'heading');
      /* CORRECTION : aria-level="3" */
      treeTitle.setAttribute('aria-level', '3');
      treeTitle.textContent = rootModalite;
      wrapper.insertBefore(treeTitle, breadcrumbArea);

      if (rootResume) {
        const treeSummary = document.createElement('p');
        treeSummary.className = 'tag-arbre-summary';
        treeSummary.textContent = rootResume;
        wrapper.insertBefore(treeSummary, breadcrumbArea);
      }
    }

    renderLevel(rootLi);
    wrapper.classList.add('is-enhanced');

    /* ── Version imprimable ──────────────────────────────────────────────────
       Generee une seule fois a l'init, a partir du <li> racine original,
       a plat et entierement depliee. Masquee a l'ecran, visible a l'impression
       (classe print__only), voir _print.scss pour le detail des styles et le
       masquage du widget interactif en impression.
    ───────────────────────────────────────────────────────────────────────── */
    insertPrintOutline(rootLi, wrapper, treeIndex);
  }

  /* ─── Démarrage : trouve tous les <ul data-profondeur="0"> et les initialise ── */
  function boot() {
    const roots = [...document.querySelectorAll('[data-profondeur="0"]')];
    roots.forEach((ul, idx) => initTree(ul, idx));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();